/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, RouteComponentProps } from '@reach/router';
import Input from '../../components/Input';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import { useGetProject, useEditProjects, ProjectDTO, ProjectAddress } from '../../queries/Projects';
import { Tab, TabsContainer } from '../../components/Tabs';
import {
    Role,
    useCreateUserRole,
    useDeleteUserRoles,
    useGetRoles,
    useGetUserRolesByRoleDTO,
    UserRole,
} from '../../queries/Roles';
import TypeAhead from '../../components/TypeAhead';
import Button from '../../components/Button';
import Chips from '../../components/Chips';
import { useGetUsers, User } from '../../queries/Users';
import Table from '../../components/Table';
import { TrashCan } from '../../components/Icons';
import { useGetPermissions } from '../../queries/Permissions';
import Modal from '../../components/Modal';
import Checkbox from '../../components/Checkbox';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function ProjectDetail(props: RouteComponentProps) {
    const params = useParams();
    const projectId = params.id;

    const newUserRole = useCreateUserRole();
    const deleteUserRoles = useDeleteUserRoles();
    const project = useGetProject(projectId);
    const roles = useGetRoles();
    const users = useGetUsers();
    const usersRef = useRef(users);

    useEffect(() => {
        usersRef.current.reset();
    }, []);

    const editProject = useEditProjects();

    const [tabs, setTabs] = useState([] as Tab[]);
    const [selectedTab, setSelectedTab] = useState('');

    const [openAddressModal, setOpenAddressModal] = useState(false);

    const userRoles = useGetUserRolesByRoleDTO({
        projectId,
        roleName: selectedTab,
    });
    const userRolesRef = useRef(userRoles);
    const [isLoading, setIsLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const { data } = project;
    const [name, setName] = useState(undefined as string | undefined);
    const [jobCode, setJobCode] = useState(undefined as string | undefined);
    const [currencies, setCurrencies] = useState(undefined as string[] | undefined);
    const [vats, setVats] = useState(undefined as string[] | undefined);
    const [addresses, setAddresses] = useState(undefined as ProjectAddress[] | undefined);
    const [tel, setTel] = useState(undefined as string | undefined);
    const [fax, setFax] = useState(undefined as string | undefined);
    const [email, setEmail] = useState(undefined as string | undefined);
    const [cell, setCell] = useState(undefined as string | undefined);
    const [contactPerson, setContactPerson] = useState(undefined as string | undefined);

    // address
    const [addressIndex, setAddressIndex] = useState(-1);
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [address3, setAddress3] = useState('');
    const [postCode, setPostCode] = useState('');

    const [isOverhead, setIsOverhead] = useState(false);

    const projectRef = useRef(project);

    const getPermissions = useGetPermissions('PROJECT', projectId);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        setName(project.data?.name);
        setJobCode(project.data?.jobCode);
        setCurrencies(project.data?.currencies?.map((i) => i.symbol) as string[]);
        setVats(project.data?.vats?.map((i) => i.amount?.toString()) as string[]);
        setCell(project.data?.cell);
        setContactPerson(project.data?.contactPerson);
        setAddresses(project.data?.addresses);
        setTel(project.data?.tel);
        setFax(project.data?.fax);
        setEmail(project.data?.email);
        setIsOverhead(project.data?.isOverhead || false);
    }, [project.data]);

    useEffect(() => {
        projectRef.current.reset();
    }, []);

    useEffect(() => {
        projectRef.current.reset();
    }, []);

    useEffect(() => {
        if (roles.data && tabs.length === 0) {
            const appRoles = roles.data
                .filter((f: Role) => f.scope === 'PROJECT') // project scoped
                .sort((a: Role, b: Role) => a.displayOrder - b.displayOrder)
                .map((roleDto: Role) => {
                    return { label: roleDto.name, id: roleDto.name };
                });
            setTabs(appRoles);
            if (appRoles.length > 0) {
                setSelectedTab(appRoles[0].label);
            }
        }
    }, [tabs, roles.data, userRolesRef]);

    useEffect(() => {
        userRolesRef.current.reset();
    }, [selectedTab, userRolesRef]);

    const [selectedUser, setSelectedUser] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const addAddress = (i: number) => {
        setOpenAddressModal(true);
        setAddress1('');
        setAddress2('');
        setAddress3('');
        setPostCode('');
        setAddressIndex(-1);

        if (i > -1 && addresses && addresses?.length > i) {
            const address = addresses[i];
            setAddressIndex(i);
            setAddress1(address.address1 || '');
            setAddress2(address.address2 || '');
            setAddress3(address.address3 || '');
            setPostCode(address.postCode || '');
        }
    };

    const upsertAddress = () => {
        const tempAddresses = addresses || [];
        if (addressIndex > -1) {
            tempAddresses[addressIndex].address1 = address1;
            tempAddresses[addressIndex].address2 = address2;
            tempAddresses[addressIndex].address3 = address3;
            tempAddresses[addressIndex].postCode = postCode;
            setAddresses(tempAddresses);
        } else {
            const newAddress: ProjectAddress = {
                address1,
                address2,
                address3,
                postCode,
            };
            const newAddresses = tempAddresses.concat(newAddress);
            setAddresses(newAddresses);
        }

        setOpenAddressModal(false);
    };

    const deleteAddress = (index: number) => {
        const tempAddresses = addresses || [];
        setAddresses(tempAddresses.filter((a, i) => i !== index));
        setOpenAddressModal(false);
    };

    const formatUsers = () => {
        if (users && users.data) {
            return users.data.map((user: User) => ({
                id: user.id,
                name: `${user.userName}${
                    user.firstName && user.lastName ? ` (${user.firstName} ${user.lastName})` : ''
                }`,
            }));
        }
        return [];
    };

    const handleSelectedItem = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const { id } = (event.target as unknown) as { id: string };
        setSelectedUser(id);
    };

    const post = async () => {
        setErrorMessage('');
        if (!selectedUser) {
            setErrorMessage('Please capture a User Name');
        } else {
            const user = users.data?.find((u: User) => u.id === selectedUser);
            if (!user) return;
            await newUserRole.execute({
                userName: user.userName,
                projectId,
                businessUnitId: project.data?.businessUnit?.id,
                roleName: selectedTab,
            });
            userRoles.reset();
            users.reset();
        }
    };

    const deleteUserRole = async (userRole: UserRole) => {
        await deleteUserRoles.execute(userRole);
        userRoles.reset();
    };

    const clearFields = () => {
        setErrorMessage('');
        setSelectedUser('');
    };

    const put = async () => {
        setIsLoading(true);
        setErrorMessage('');
        const projectDTO = {
            name,
            currencies,
            vats: vats?.map((i) => parseFloat(i)),
            jobCode,
            id: project.data?.id,
            businessUnitId: project.data?.businessUnit?.id,
            addresses,
            cell,
            contactPerson,
            email,
            fax,
            tel,
            isOverhead,
        } as ProjectDTO & { id: string };
        await editProject.execute(projectDTO);
        project.reset();
        setIsLoading(false);
        setEditing(false);
    };

    const handleEdit = () => {
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        project.reset();
    };

    const renderProject = () => {
        if (!project || project.loading) {
            return <Loader message="Fetching Project from server" />;
        }
        if (project.error) {
            return <Error message={`Failed to fetch Project from the server (${project.error})`} />;
        }
        if (data) {
            const { id } = data;
            return (
                <>
                    <div className="flex items-center justify-between gap-4 p-4 border-b border-border">
                        <div className="text-lg font-semibold text-foreground">
                            Project Information
                        </div>
                    </div>
                    <div className="p-4 space-y-4">
                        <div hidden>
                            <Input label="id" value={id || ''} disabled />
                        </div>
                        <Input
                            label="Name"
                            value={name}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <Input
                            label="Job Code"
                            value={jobCode}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setJobCode(e.target.value)}
                        />
                        <Chips
                            label="Currencies"
                            value={currencies}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setCurrencies(e)}
                        />
                        <Chips
                            label="VAT"
                            value={vats}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setVats(e)}
                        />
                        <Input
                            label="Tel"
                            value={tel || ''}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setTel(e.target.value)}
                        />
                        <Input
                            label="Fax"
                            value={fax || ''}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setFax(e.target.value)}
                        />
                        <Input
                            label="Email"
                            value={email || ''}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <Input
                            label="Cell"
                            value={cell || ''}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setCell(e.target.value)}
                        />
                        <Input
                            label="Contact Person"
                            value={contactPerson || ''}
                            type="text"
                            disabled={!editing}
                            onChange={(e) => setContactPerson(e.target.value)}
                        />
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-semibold text-foreground-secondary">
                                    Addresses
                                </div>
                                {editing && <Button onClick={() => addAddress(-1)}>Add</Button>}
                            </div>
                            {(!addresses || addresses.length === 0) && (
                                <div className="px-3 py-2 text-sm text-foreground-muted bg-surface-muted border border-border rounded-lg">
                                    No addresses captured yet.
                                </div>
                            )}
                            <div className="space-y-3">
                                {addresses &&
                                    addresses.map((a, i) => (
                                        <div
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={i}
                                            className="bg-surface-muted border border-border rounded-lg p-3 space-y-3"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-semibold text-foreground">
                                                    Address {i + 1}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {editing && (
                                                        <Button onClick={() => addAddress(i)}>
                                                            Edit
                                                        </Button>
                                                    )}
                                                    {editing && (
                                                        <Button onClick={() => deleteAddress(i)}>
                                                            Delete
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <Input
                                                label="Address Line 1"
                                                value={a.address1 || ''}
                                                type="text"
                                                disabled
                                            />
                                            <Input
                                                label="Address Line 2"
                                                value={a.address2 || ''}
                                                type="text"
                                                disabled
                                            />
                                            <Input
                                                label="Address Line 3"
                                                value={a.address3 || ''}
                                                type="text"
                                                disabled
                                            />
                                            <Input
                                                label="Post Code"
                                                value={a.postCode || ''}
                                                type="text"
                                                disabled
                                            />
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <Checkbox
                            label="Overhead"
                            name="overheads"
                            onChange={() => setIsOverhead(!isOverhead)}
                            value={isOverhead}
                            disabled={!editing}
                        />
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                            {editing && (
                                <Button onClick={put}>
                                    {!isLoading ? 'Submit' : <div>Loading</div>}
                                </Button>
                            )}
                            {!editing && <Button onClick={handleEdit}>Edit</Button>}
                            {editing && <Button onClick={cancelEdit}>Cancel</Button>}
                        </div>
                    </div>
                </>
            );
        }
        return null;
    };

    return (
        <div className="flex">
            <Modal
                title="Address"
                onClose={() => setOpenAddressModal(false)}
                isOpen={openAddressModal}
                onAction={upsertAddress}
            >
                <div className="space-y-4 w-80">
                    <Input
                        label="Address Line 1"
                        value={address1 || ''}
                        type="text"
                        disabled={!editing}
                        onChange={(e) => setAddress1(e.target.value)}
                    />
                    <Input
                        label="Address Line 2"
                        value={address2 || ''}
                        type="text"
                        disabled={!editing}
                        onChange={(e) => setAddress2(e.target.value)}
                    />
                    <Input
                        label="Address Line 3"
                        value={address3 || ''}
                        type="text"
                        disabled={!editing}
                        onChange={(e) => setAddress3(e.target.value)}
                    />
                    <Input
                        label="Post Code"
                        value={postCode || ''}
                        type="text"
                        disabled={!editing}
                        onChange={(e) => setPostCode(e.target.value)}
                    />
                </div>
            </Modal>
            <div className="bg-surface border border-border rounded-xl w-1/4 m-4">
                {renderProject()}
            </div>
            <div className="bg-surface border border-border rounded-xl w-3/4 m-4">
                <div className="flex items-center justify-between gap-4 p-4 border-b border-border">
                    <div className="text-lg font-semibold text-foreground">Project Roles</div>
                </div>
                <div className="p-4">
                    <TabsContainer
                        selectedTab={selectedTab}
                        options={tabs}
                        onClick={setSelectedTab}
                    >
                        <div className="mt-5 w-full">
                            <TypeAhead
                                placeholder="Search"
                                caseSensitive={false}
                                label="Users"
                                options={formatUsers()}
                                onClick={(event) => handleSelectedItem(event)}
                            />
                            <div className="mb-5 mt-4">
                                <div className="flex items-center justify-between gap-4">
                                    {errorMessage && (
                                        <div className="text-sm text-danger">{errorMessage}</div>
                                    )}
                                    <div className="flex items-center gap-2 ml-auto">
                                        <Button onClick={clearFields}>Clear</Button>
                                        <Button onClick={post}>Submit</Button>
                                    </div>
                                </div>
                            </div>
                            {((userRoles.data && userRoles.data.length > 0) ||
                                userRoles.loading ||
                                userRoles.error) && (
                                <Table<UserRole>
                                    loading={userRoles.loading && 'Fetching UserRoles from server'}
                                    error={
                                        userRoles.error &&
                                        `Failed to fetch UserRole list from the server (${userRoles.error})`
                                    }
                                    data={userRoles.data}
                                    columns={[
                                        { key: 'userName', title: 'User Name' },
                                        { key: 'roleName', title: 'Role Name' },
                                        {
                                            key: 'delete',
                                            title: '',
                                            render: (userRole) => (
                                                <TrashCan
                                                    className="cursor-pointer pl-2"
                                                    id={userRole.userName}
                                                    onClick={() => deleteUserRole(userRole)}
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            )}
                            {userRoles.data &&
                                userRoles.data.length === 0 &&
                                !userRoles.loading &&
                                !userRoles.error && (
                                    <div className="px-3 py-2 text-sm text-foreground-muted bg-surface-muted border border-border rounded-lg">
                                        There are currently no UserRoles. Please add one.
                                    </div>
                                )}
                        </div>
                    </TabsContainer>
                </div>
            </div>
        </div>
    );
}
